import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import '@fontsource/bebas-neue';
import { styled, keyframes } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Skeleton from '@mui/material/Skeleton';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import StationStatus from '../components/StationStatus';

const ACCENT_PRIMARY = '#ff6600';
const ACCENT_SECONDARY = '#92abcf';
const GRADIENT_PRIMARY = 'linear-gradient(135deg, rgba(255,102,0,0.14), rgba(255,102,0,0.02))';
const GRADIENT_SECONDARY = 'linear-gradient(135deg, rgba(146,171,207,0.14), rgba(146,171,207,0.02))';
const GRADIENT_NEUTRAL = 'linear-gradient(135deg, rgba(255,255,255,0.07), rgba(255,255,255,0.01))';

const flashAnimation = keyframes`
  0% { opacity: 1; }
  70% { opacity: 1; }
  72.5% { opacity: 0; }
  75% { opacity: 1; }
  77.5% { opacity: 0; }
  80% { opacity: 1; }
  82.5% { opacity: 0; }
  85% { opacity: 1; }
  87.5% { opacity: 0; }
  90% { opacity: 1; }
  100% { opacity: 1; }
`;

const HeroLogoSVG = styled('svg')(({ theme }) => ({
  width: '1em',
  height: '1em',
  display: 'inline-block',
  verticalAlign: 'text-top',
  marginRight: '0.4rem',
  flexShrink: 0,
  fill: theme.palette.primary.main,
  opacity: 1,
  animation: `${flashAnimation} 30s infinite`,
}));

const sectionVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
};

const sectionTitleSx = {
  fontSize: 'clamp(1.75rem, 4.5vw, 2.75rem)',
  fontWeight: 700,
  letterSpacing: '-0.01em',
  lineHeight: 1,
  marginBottom: 2.5,
};

function Section({ children, accent, gradient, sx }) {
  return (
    <Box
      component={motion.div}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      variants={sectionVariants}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      sx={{
        height: '100%',
        padding: { xs: '1.75rem 1.5rem', md: '2rem 2rem' },
        background: gradient || GRADIENT_NEUTRAL,
        border: '1px solid rgba(255,255,255,0.08)',
        borderTop: `3px solid ${accent || 'rgba(255,255,255,0.15)'}`,
        borderRadius: '4px',
        transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
        '&:hover': {
          transform: 'translateY(-6px)',
          borderColor: accent ? `${accent}66` : 'rgba(255,255,255,0.25)',
          boxShadow: accent ? `0 16px 28px ${accent}26` : '0 16px 28px rgba(255,255,255,0.08)',
        },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

const ctaButtonSx = {
  fontSize: { xs: '0.9rem', md: '1rem' },
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

function About() {
  const [loading, setLoading] = useState(true);
  const generalContactUrl = 'https://forms.gle/4AJvTdm8tEVaL7ZZ7';
  const submitShowUrl = 'https://forms.gle/1t76mzPehGXxUBYWA';
  const deck1PlsUrl = 'https://wssr.stream/other/wssr_deck1.pls';
  const deck2PlsUrl = 'https://wssr.stream/other/wssr_deck2.pls';

  useEffect(() => {
    const hasVisited = localStorage.getItem('hasVisitedAboutPage');

    if (hasVisited) {
      setLoading(false);
    } else {
      setTimeout(() => {
        setLoading(false);
        localStorage.setItem('hasVisitedAboutPage', 'true');
      }, 1500);
    }
  }, []);

  if (loading) {
    return (
      <Box sx={{ marginBottom: '8rem' }}>
        <Skeleton variant="text" width="90%" height={160} />
        <Skeleton variant="text" width="60%" height={50} sx={{ mb: 6 }} />
        <Skeleton variant="text" width="30%" height={80} />
        <Skeleton variant="text" width="50%" height={40} sx={{ mb: 6 }} />
        <Skeleton variant="text" width="40%" height={80} />
        <Skeleton variant="rectangular" width="100%" height={80} sx={{ mb: 6 }} />
        <Skeleton variant="text" width="25%" height={80} />
        <Skeleton variant="rectangular" width="100%" height={60} />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          position: 'relative',
          paddingTop: { xs: '1.5rem', md: '2.5rem' },
          marginBottom: { xs: '5rem', md: '8rem' },
        }}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <Typography
            component="h1"
            sx={{
              fontFamily: '"Bebas Neue", sans-serif',
              fontSize: 'clamp(1.5rem, 10vw, 22rem)',
              fontWeight: 400,
              letterSpacing: '0.01em',
              lineHeight: 0.8,
              color: 'transparent',
              WebkitTextStroke: { xs: `1.5px ${ACCENT_PRIMARY}`, md: `3px ${ACCENT_PRIMARY}` },
              margin: 0,
              animation: 'hero-static-flicker 6s linear infinite',
            }}
          >
            <Box component="span" sx={{ display: 'block' }}>Sector</Box>
            <Box component="span" sx={{ display: 'block' }}>Seven</Box>
            <Box component="span" sx={{ display: 'block' }}>Radio</Box>
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Bebas Neue", sans-serif',
              fontSize: 'clamp(1.2rem, 3vw, 1.75rem)',
              fontWeight: 300,
              lineHeight: 1.2,
              color: 'rgba(255,255,255,0.7)',
              maxWidth: '65ch',
            }}
          >
            <HeroLogoSVG xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <rect x="4" y="4" width="40" height="40" />
            </HeroLogoSVG>
            Independent 27/7 Broadcast
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          alignItems: 'stretch',
          gap: 3,
          marginBottom: '8rem',
        }}
      >
        <Section accent={ACCENT_PRIMARY} gradient={GRADIENT_PRIMARY}>
          <Typography component="h2" sx={sectionTitleSx}>
            <Box component="span" sx={{ color: 'primary.main' }}>Stream</Box> Info
          </Typography>

          <Stack direction="column" alignItems="stretch" spacing={1.5}>
            <Button href={generalContactUrl} target="_blank" variant="contained" fullWidth sx={ctaButtonSx}>
              General Contact Form
            </Button>
            <Button href={submitShowUrl} target="_blank" variant="outlined" fullWidth sx={ctaButtonSx}>
              Submit Your Show
            </Button>
          </Stack>
        </Section>

        <Section accent={ACCENT_SECONDARY} gradient={GRADIENT_SECONDARY}>
          <Typography component="h2" sx={sectionTitleSx}>
            How to <Box component="span" sx={{ color: 'secondary.main' }}>Listen</Box>
          </Typography>

          <Typography
            sx={{
              fontSize: 'clamp(0.95rem, 1.6vw, 1.1rem)',
              fontWeight: 300,
              lineHeight: 1.5,
              marginBottom: 3,
            }}
          >
            You can listen right here on wssr.stream, via our{' '}
            <Link to="/partners" style={{ color: 'inherit', textDecoration: 'underline' }}>
              partner sites
            </Link>
            , or through VLC / Winamp / iTunes &amp; other audio software.
          </Typography>

          <ButtonGroup variant="text" aria-label="text button group">
            <Button href={deck1PlsUrl} target="_blank" download="DECK1.pls" startIcon={<CloudDownloadIcon />} sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' }, letterSpacing: '0.04em' }}>
              DECK 1
            </Button>
            <Button href={deck2PlsUrl} target="_blank" color="secondary" download="DECK2.pls" startIcon={<CloudDownloadIcon />} sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' }, letterSpacing: '0.04em' }}>
              DECK 2
            </Button>
          </ButtonGroup>
        </Section>

        <Section gradient={GRADIENT_NEUTRAL}>
          <Typography component="h2" sx={sectionTitleSx}>
            Status
          </Typography>

          <StationStatus />
        </Section>
      </Box>

      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{
          color: (theme) => theme.palette.primary.main,
          fontFamily: '"Bebas Neue", sans-serif',
          fontSize: 'clamp(1rem, 2.5vw, 1.5rem)',
          letterSpacing: '0.05em',
          marginBottom: '8rem',
        }}
      >
        ⊣ Sector Seven Radio Project ⊢
      </Box>
    </Box>
  );
}

export default About;
